import {matchRoutes, RoutesContext, type RouteObject} from '@github-ui/react-router'
import {sendCustomMetric} from '@github-ui/stats'
import {use, useEffect} from 'react'

import {repoLayoutRoute} from '../routes/repo-layout-route'

type PreloadableRouteComponent = {preload?: () => Promise<unknown>}
type ComponentPreload = NonNullable<PreloadableRouteComponent['preload']>

const SCAN_METRIC_SAMPLE_RATE = 0.01
const LINK_COUNT_BUCKETS: ReadonlyArray<readonly [label: string, min: number]> = [
  ['10000+', 10_000],
  ['5000-9999', 5_000],
  ['1000-4999', 1_000],
  ['500-999', 500],
  ['100-499', 100],
  ['1-99', 1],
]

function linkCountBucket(count: number) {
  for (const [label, min] of LINK_COUNT_BUCKETS) {
    if (count >= min) return label
  }
  return '0'
}

function findRoute(routes: RouteObject[], id: string): RouteObject | undefined {
  for (const route of routes) {
    if (route.id === id) return route
    const match = route.children ? findRoute(route.children, id) : undefined
    if (match) return match
  }
}

function collectComponentPreloads(
  routes: RouteObject[],
  preloads = new Set<ComponentPreload>(),
): Set<ComponentPreload> {
  for (const route of routes) {
    const component = route.Component as PreloadableRouteComponent | null | undefined
    if (component?.preload) preloads.add(component.preload)
    if (route.children) collectComponentPreloads(route.children, preloads)
  }
  return preloads
}

function componentPreloadsForRouteLink(
  link: HTMLAnchorElement,
  routes: RouteObject[],
  repositoryPath: string,
): ComponentPreload[] {
  if (!isSameRepositoryLink(link, repositoryPath)) return []

  const matches = matchRoutes(routes, link.pathname)
  if (!matches) return []

  const leafRoute = matches.at(-1)?.route
  if (!leafRoute || leafRoute.children?.length) return []

  const preloads = new Set<ComponentPreload>()
  for (const match of matches) {
    const component = match.route.Component as PreloadableRouteComponent | null | undefined
    if (component?.preload) preloads.add(component.preload)
  }
  return [...preloads]
}

function isSameRepositoryLink(link: HTMLAnchorElement, repositoryPath: string) {
  if (link.origin !== window.location.origin) return false

  const pathname = link.pathname.toLowerCase()
  return pathname === repositoryPath || pathname.startsWith(`${repositoryPath}/`)
}

async function preloadComponent(preload: ComponentPreload) {
  try {
    await preload()
  } catch {
    // Speculative preloading is best-effort; normal route rendering remains the fallback.
  }
}

function observeVisibleRouteLinks(routes: RouteObject[], owner: string, repo: string): () => void {
  const allPreloads = collectComponentPreloads(routes)
  if (allPreloads.size === 0 || typeof IntersectionObserver === 'undefined') return () => {}

  const requestedPreloads = new Set<ComponentPreload>()
  const repositoryPath = `/${owner}/${repo}`.toLowerCase()
  const pendingLinkRoots = new Set<Element>()
  let disconnected = false
  let disconnect = () => {}
  let cancelPendingLinkScan: (() => void) | undefined

  const intersectionObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (disconnected || !entry.isIntersecting) continue

      const link = entry.target as HTMLAnchorElement
      intersectionObserver.unobserve(link)
      for (const preload of componentPreloadsForRouteLink(link, routes, repositoryPath)) {
        if (requestedPreloads.has(preload)) continue
        requestedPreloads.add(preload)
        void preloadComponent(preload)
        if (requestedPreloads.size === allPreloads.size) {
          disconnect()
          return
        }
      }
    }
  })

  const observeLink = (link: HTMLAnchorElement) => {
    if (disconnected) return false
    if (link.isConnected && link.hasAttribute('href') && isSameRepositoryLink(link, repositoryPath)) {
      intersectionObserver.observe(link)
      return true
    } else {
      intersectionObserver.unobserve(link)
      return false
    }
  }

  const scanLinkRoot = (root: Element) => {
    if (root instanceof HTMLAnchorElement) observeLink(root)
    for (const link of root.getElementsByTagName('a')) observeLink(link)
  }

  const scanPendingLinks = () => {
    cancelPendingLinkScan = undefined
    for (const root of pendingLinkRoots) scanLinkRoot(root)
    pendingLinkRoots.clear()
  }

  const schedulePendingLinkScan = () => {
    if (cancelPendingLinkScan || disconnected) return
    cancelPendingLinkScan = scheduleWhenIdle(scanPendingLinks)
  }

  const mutationObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof HTMLAnchorElement) pendingLinkRoots.add(mutation.target)
        continue
      }

      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLAnchorElement || (node instanceof Element && node.childElementCount > 0)) {
          pendingLinkRoots.add(node)
        }
      }
      for (const node of mutation.removedNodes) {
        if (node instanceof HTMLAnchorElement || (node instanceof Element && node.childElementCount > 0)) {
          pendingLinkRoots.add(node)
        }
      }
    }
    schedulePendingLinkScan()
  })
  disconnect = () => {
    if (disconnected) return
    disconnected = true
    cancelPendingLinkScan?.()
    pendingLinkRoots.clear()
    mutationObserver.disconnect()
    intersectionObserver.disconnect()
  }
  mutationObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['href'],
    childList: true,
    subtree: true,
  })

  const scanStartedAt = performance.now()
  let linkCount = 0
  let eligibleLinkCount = 0
  for (const link of document.links) {
    if (!(link instanceof HTMLAnchorElement)) continue
    linkCount++
    if (observeLink(link)) eligibleLinkCount++
  }
  sendCustomMetric(
    {
      name: 'REPO_VISIBLE_ROUTE_LINK_SCAN_DURATION',
      value: Math.max(0, performance.now() - scanStartedAt),
      tags: {
        linkCountBucket: linkCountBucket(linkCount),
        eligibleLinkCountBucket: linkCountBucket(eligibleLinkCount),
      },
    },
    false,
    SCAN_METRIC_SAMPLE_RATE,
  )

  return disconnect
}

function scheduleWhenIdle(callback: () => void): () => void {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(callback)
    return () => cancelIdleCallback(id)
  }

  const id = setTimeout(callback, 0)
  return () => clearTimeout(id)
}

/**
 * Preloads component code, but not route data, for same-repository links as they enter the viewport.
 * Setup and incremental link discovery are deferred until idle, and observation stops as soon as
 * every preloadable component in the enabled repo route tree has been requested.
 */
export function usePreloadVisibleRouteComponents(owner: string | undefined, repo: string | undefined) {
  const {routes} = use(RoutesContext)

  useEffect(() => {
    if (!owner || !repo) return

    const subAppRoutes = findRoute(routes, repoLayoutRoute.id)?.children
    if (!subAppRoutes?.length) return

    let disconnect: (() => void) | undefined
    const cancelSchedule = scheduleWhenIdle(() => {
      disconnect = observeVisibleRouteLinks(subAppRoutes, owner, repo)
    })
    return () => {
      cancelSchedule()
      disconnect?.()
    }
  }, [owner, repo, routes])
}
